import { Estimate, MailQueue } from '@/domain/Estimate'
import axios from 'axios'
import { SendMail } from '@/domain/SendMail'

/**
 * 検索条件
 */
export class SerchConditon {
    /** 見積番号 */
    estimateNo = ""
    /** 期間開始日 */
    startOn = ""
    /** 期間終了日 */
    endOn = ""
    /** 顧客都道府県コード */
    customerPrefectureCode = ""
    /** 顧客コード */
    customerCode = ""
    /** 顧客枝番 */
    customerBranchNumber = ""
    /** 顧客名 */
    customerName = ""
    /* 有効 */
    isActive = true

    /* 画面用 */
    /** 期間タイプ */
    dateType = 3

    /**
     * コンストラクタ
     * @param init 
     */
    constructor(init?: Partial<SerchConditon>) {
        Object.assign(this, init)
    }
}

/**
 * 見積サービス
 */
export default class EstimatesService {
    /** インスタンス */
    private static _instance: EstimatesService

    /**
     * コンストラクタ
     */
    // eslint-disable-next-line
    private constructor() {}

    /**
     * インスタンスの取得
     */
    public static get instance() {
        if (!this._instance) {
            this._instance = new EstimatesService();
        }
        return this._instance;
    }
    
    /**
     * 新規作成
     */
    public async createNew(): Promise<Estimate> {
        return await axios.get("/api/estimate/new")
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }
    
    /**
     * 参照新規作成
     */
    public async referenceNew(requestNos: string[]): Promise<Estimate> {
        const params = requestNos.map(x => `requestNos=${x}`).join("&")
        return await axios.get(`/api/estimate/new?${params}`)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }

    /**
     * 複写作成
     */
    public async copyNew(estimateNo: string): Promise<Estimate> {
        return await axios.get(`/api/estimate/copy_new?estimateNo=${estimateNo}`)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }

    /**
     * 編集
     */
    public async edit(no: string): Promise<Estimate> {
        return await axios.get(`/api/estimate/edit/${no}`)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 新規保存
     */
    public async create(dto: Estimate): Promise<Estimate> {
        return await axios.post("/api/estimate/new", dto)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 保存
     */
    public async update(dto: Estimate): Promise<Estimate> {
        return await axios.put(`/api/estimate/edit/${dto.estimateNo}`, dto)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 削除
     */
    public async delete(no: string): Promise<Estimate> {
        return await axios.delete(`/api/estimate/delete/${no}`)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 印刷
     */
    public async print(no: string): Promise<Estimate> {
        return await axios.put(`/api/estimate/print/${no}`)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 見積一覧を検索
     */
    public async findAll(serchConditon: SerchConditon): Promise<Estimate[]> {
        return await axios.get("/api/estimates", {params: serchConditon})
            .then((results) => {
                if (results.status === 200) {
                    return (results.data as Estimate[]).map(x => this.convert(x))
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * メール送信テンプレートの取得
     */
    public async generateMailTemplate(nos: string[]): Promise<SendMail> {
        const params = nos.map(x => `no=${x}`).join("&")
        return await axios.get(`/api/estimates/generate_mail_template?${params}`)
            .then((results) => {
                if (results.status === 200) {
                    const sendMail = results.data as SendMail
                    return new SendMail({ ...sendMail })
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * メール送信キューへの登録
     */
    public async mailQueuePush(mailQueue: MailQueue): Promise<MailQueue> {
        return await axios.post("/api/estimates/mail_queue_push", mailQueue)
        .then((results) => {
            if (results.status === 200) {
                return results.data as MailQueue
            } else {
                throw results.statusText
            }
        })
        .catch((error) => {
            throw error
        })
    }

    /**
     * 受信データの変換
     * @param data
     */
    // eslint-disable-next-line
    private convert(data: any): Estimate {
        const estimate = data as Estimate
        return new Estimate({ ...estimate })
    }
}
